/** The card scan (STORY-089 M3): the teacher photographs the raised cards, the phone reads
 * them, and each card's answer goes to the open round on channel `card`.
 *
 * - **The page never sends or keeps the photo.** It is decoded here (`cards/pixels.ts`, a
 *   worker), drawn on this sheet's canvas, and dropped: the full-size bitmap is closed once the
 *   decoder's pixels and a small preview are taken, before the decode is awaited, and the canvas
 *   is emptied when the sheet closes. Nothing is uploaded, logged or stored by the page; only
 *   (card, letter) is sent, as an answer. What the platform does with a capture is its own
 *   (`cardCamera.ts`).
 * - **The controller outlives the sheet.** `useCardScan` keeps one per lesson in the live view,
 *   so closing the sheet loses no queued answer: the posts go on, and a reopened sheet shows the
 *   round they belong to. Only the live view closing disposes of it.
 * - **Marks.** In a named session each card seen gets its printed number, never its letter
 *   (`marksFor`): the scan is offered only on a coarse pointer, but a phone's screen can still
 *   be seen, and the letter counts are in the report. In an anonymous one every mark is
 *   neutral, read or seen-but-not-read. The text gives counts, and the printed numbers of the
 *   cards left out (for want of a seat, or because their label is someone else's), never a
 *   letter beside a number.
 * - **More photos** add to the same round; a card's answer is posted again only when it
 *   changed. Nothing here goes to the projector.
 * - **The round** is fixed at the tap, and the round just closed stays on the sheet: see
 *   `cardScan.ts`, the controller a test drives.
 * - **The card rows** (M4) are read when the sheet opens and again with each photo, with the
 *   teacher's own token on the host the live view talks to (on a paired phone, the laptop's
 *   rows). No photo is taken and no card joins until the first read has answered; a failed or
 *   timed-out read offers to read again, and a refused one says the list is not this account's
 *   (or, for a host without the route, that the host has none). A failed read beside a photo
 *   costs that photo only. A card mapped to a seat joins with that seat as its label; in a named
 *   session of a course with seat rows a card with no seat is left out, and the report lists its
 *   number. An anonymous session joins every card by its number.
 * - **One label, one respondent.** The live view hands the sheet its staff frame's labels, and a
 *   card whose label someone in the session already has is left out unless it is that someone
 *   (`cardScan.ts`, `gateway.ts`).
 *
 * The words live in `CardScanReport`, which a test renders without the portal. */
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { CAPTURE_WINDOW_MS, CardGateway, MAX_AGE_MS, type GatewayOptions, type GatewayView, type RoundItem, type RoundSummary, type Stop } from "../cards/gateway";
import type { CardScan } from "../cards/decode";
import { bitmapOf, decodeOffThread, fitWithin, pixelsOf } from "../cards/pixels";
import { capabilities } from "../platform/capabilities";
import type { CardCamera } from "../platform/cardCamera";
import { listCourseDevices, mintGatewayLease } from "../teacher/api";
import { Button, Sheet } from "../ui";
import { CardScanController, marksFor, readPhotoWith, reportOf, type ReadPhoto, type RowsState, type ScanBlock, type ScanNotice, type ScanPhase } from "./cardScan";

const LETTERS = ["A", "B", "C", "D"];
/** The drawn photo's long side: enough to see a card, far less than the camera's. */
const SHOWN_SIDE = 1280;
/** The status sentence a screen reader hears waits this long for the counts to settle. */
export const STATUS_SETTLE_MS = 1_000;

export type { RowsState, ScanBlock, ScanNotice, ScanPhase } from "./cardScan";

/** The course's card rows, read with the teacher's own token through `api()`: never the
 * lease, which is not a principal and never reaches the device routes. */
export const cardRowsOf = (courseId: string, list: typeof listCourseDevices = listCourseDevices) => (signal: AbortSignal) => list(courseId, "card", signal);

type ReportProps = {
  anonymous: boolean;
  /** The open item's option keys, in order: the edge printed A answers the first. */
  keys: string[];
  phase: ScanPhase;
  blocked: ScanBlock;
  stopped: Stop | null;
  /** Why the last photo was not read, if it was not. */
  notice?: ScanNotice;
  /** The round last scanned; null before the first read. */
  summary: RoundSummary | null;
  /** The summary is of a round that is no longer open: the round just closed. */
  previous?: boolean;
  /** The last photo's own counts. */
  photo: { read: number; unread: number; duplicates: number } | null;
  /** The read of the course's card rows (M4); a card joins only once it is `ready`. */
  rows?: RowsState;
  /** Cards of this round that joined, and whose row names a student: they joined by their
   * numbers. */
  byNumber?: number;
  /** Read the card rows again, after a failed read. */
  onRetry?: () => void;
};

/** The one sentence a screen reader is told as the sheet changes: what is happening, or how
 * many cards this round has read. The rest of the report is read when the teacher moves to it;
 * the stop, a failure and a notice are alerts of their own. */
export function statusSentence(
  t: (key: string, o?: Record<string, unknown>) => string,
  { phase, blocked, stopped, summary, rows = "ready", previous = false }: Pick<ReportProps, "phase" | "blocked" | "stopped" | "summary" | "rows" | "previous">,
): string {
  if (stopped) return "";
  if (rows === "reading") return t("cards.scan.rows.reading");
  if (phase === "reading") return t("cards.scan.reading");
  if (blocked) return t(`cards.scan.${blocked}`);
  if (!summary || summary.read === 0) return "";
  const read = t("cards.scan.read", { count: summary.read });
  if (summary.inFlight === 0) return read;
  return `${read}. ${t(previous ? "cards.scan.inFlightClosed" : "cards.scan.inFlight", { count: summary.inFlight })}`;
}

/** `value`, once it has held for `ms`: the first value at once. */
function useSettled(value: string, ms: number): string {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    if (value === shown) return;
    const timer = setTimeout(() => setShown(value), ms);
    return () => clearTimeout(timer);
  }, [value, shown, ms]);
  return shown;
}

/** What the sheet says, named or anonymous: counts, and the printed numbers of the cards left
 * out; never a card's number beside its letter, and never the server's own words. */
export function CardScanReport({ anonymous, keys, phase, blocked, stopped, notice = null, summary, previous = false, photo, rows = "ready", byNumber = 0, onRetry }: ReportProps) {
  const { t } = useTranslation();
  const status = useSettled(statusSentence(t, { phase, blocked, stopped, summary, rows, previous }), STATUS_SETTLE_MS);
  const shown = keys.slice(0, 4);
  const legend = shown.some((k, i) => k !== LETTERS[i]);
  const seconds = MAX_AGE_MS / 1000;
  const lines: string[] = [];
  if (summary) {
    if (summary.beyond) lines.push(t("cards.scan.beyond", { count: summary.beyond }));
    if (summary.tooOld) lines.push(t("cards.scan.tooOld", { count: summary.tooOld, seconds }));
    for (const r of summary.refused) lines.push(t(`cards.scan.refused.${r.refusal}`, { count: r.count }));
  }
  if (summary && byNumber) lines.push(t("cards.scan.byNumber", { count: byNumber }));
  // the cards left out for want of a seat: their numbers only, never a letter or a name
  if (summary?.unmapped.length) lines.push(t("cards.scan.unmapped", { count: summary.unmapped.length, cards: summary.unmapped.join(", ") }));
  if (summary?.taken.length) lines.push(t("cards.scan.taken", { count: summary.taken.length, cards: summary.taken.join(", ") }));
  if (photo?.unread) lines.push(t("cards.scan.unread", { count: photo.unread }));
  if (photo?.duplicates) lines.push(t("cards.scan.duplicates", { count: photo.duplicates }));
  const noticeText = notice === "oldPhoto" ? t("cards.scan.notice.oldPhoto", { count: CAPTURE_WINDOW_MS / 60_000 }) : notice ? t(`cards.scan.notice.${notice}`, { seconds }) : null;
  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="sr-only">{status}</p>
      {stopped && <p role="alert" style={{ color: "var(--danger)" }}>{t(`cards.scan.stop.${stopped}`)}</p>}
      {!stopped && rows === "reading" && <p>{t("cards.scan.rows.reading")}</p>}
      {!stopped && rows === "denied" && <p role="alert">{t("cards.scan.rows.denied")}</p>}
      {!stopped && rows === "missing" && <p role="alert">{t("cards.scan.rows.missing")}</p>}
      {!stopped && rows === "failed" && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert">{t("cards.scan.rows.failed")}</p>
          {onRetry && <Button variant="secondary" onClick={onRetry}>{t("cards.scan.rows.retry")}</Button>}
        </div>
      )}
      {!stopped && blocked && <p>{t(`cards.scan.${blocked}`)}</p>}
      {phase === "reading" && <p>{t("cards.scan.reading")}</p>}
      {phase === "failed" && <p role="alert">{t("cards.scan.failed")}</p>}
      {noticeText && <p role="alert">{noticeText}</p>}
      {phase === "shown" && photo && photo.read === 0 && !notice && <p>{t("cards.scan.noneRead")}</p>}
      {summary && summary.read > 0 && (
        <>
          {previous && <p className="text-sm text-[color:var(--ink-2)]">{t("cards.scan.previous")}</p>}
          <p className="font-semibold">{t("cards.scan.read", { count: summary.read })}</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2" aria-label={t("cards.scan.counts")}>
            {summary.letters.map((n, i) => (
              <li key={LETTERS[i]} className="flex items-baseline gap-2">
                <span className="display text-lg">{LETTERS[i]}</span>
                <span className="num bignum text-2xl">{n}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {legend && shown.length > 0 && (
        <p className="text-sm text-[color:var(--ink-2)]">
          {shown.map((key, i) => t("cards.scan.legend", { letter: LETTERS[i], key })).join(" · ")}
        </p>
      )}
      {lines.map((line) => (
        <p key={line} className="text-sm">{line}</p>
      ))}
      {summary && summary.inFlight > 0 && (
        <p className="text-sm font-semibold">{t(previous ? "cards.scan.inFlightClosed" : "cards.scan.inFlight", { count: summary.inFlight })}</p>
      )}
      {anonymous && <p className="text-sm text-[color:var(--ink-2)]">{t("cards.scan.anonymousMarks")}</p>}
    </div>
  );
}

/** The lesson a scan belongs to. */
export type ScanLesson = { sessionId: string; courseId: string; joinCode: string; anonymous: boolean };

/** What `scanControllerFor` reaches outside itself; tests pass fakes. */
export type ScanWiring = {
  mint?: typeof mintGatewayLease;
  listDevices?: typeof listCourseDevices;
  camera?: CardCamera | null;
  fetch?: GatewayOptions["fetch"];
  now?: () => number;
  read?: (blob: Blob, signal: AbortSignal) => Promise<ReadPhoto>;
  /** The sheet's canvas, while a sheet is open. */
  canvas?: () => HTMLCanvasElement | null;
};

/** The controller for one lesson's scan: a gateway that mints for this session and joins with
 * this lesson's code and course, the course's card rows read with the teacher's own token, the
 * phone's camera, and a read that draws on the sheet's canvas. */
export function scanControllerFor(lesson: ScanLesson, wiring: ScanWiring = {}): CardScanController {
  const mint = wiring.mint ?? mintGatewayLease;
  const canvas = wiring.canvas ?? (() => null);
  return new CardScanController({
    gateway: new CardGateway({
      sessionId: lesson.sessionId,
      courseId: lesson.courseId,
      joinCode: lesson.joinCode,
      mint: (signal) => mint(lesson.sessionId, signal),
      fetch: wiring.fetch,
      now: wiring.now,
    }),
    camera: wiring.camera !== undefined ? wiring.camera : capabilities.cardCamera,
    read: wiring.read ?? ((blob, signal) => readPhoto(blob, signal, canvas, lesson.anonymous)),
    readRows: cardRowsOf(lesson.courseId, wiring.listDevices),
    anonymous: lesson.anonymous,
    now: wiring.now,
  });
}

/** The live view's scan: one controller per lesson, made when the lesson is known and the scan
 * is offered, kept while the sheet opens and closes, and disposed when the lesson or the view
 * goes. Every render hands it the live frame. */
export function useCardScan(
  lesson: ScanLesson | null,
  live: { item: RoundItem | null; skewMs: number; labels?: Readonly<Record<string, string>>; connected: boolean },
): { ctl: CardScanController | null; canvas: RefObject<HTMLCanvasElement | null> } {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [ctl, setCtl] = useState<CardScanController | null>(null);
  const key = lesson ? `${lesson.sessionId}|${lesson.courseId}|${lesson.joinCode}|${lesson.anonymous}` : null;
  useEffect(() => {
    if (!lesson) return;
    const c = scanControllerFor(lesson, { canvas: () => canvas.current });
    setCtl(c);
    return () => {
      // the live view closed: queued answers never start and nothing mints again
      c.dispose();
      setCtl(null);
    };
    // the key is the lesson
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  ctl?.setLive(live.item, live.skewMs, live.labels, live.connected);
  return { ctl, canvas };
}

type Props = {
  ctl: CardScanController;
  canvas: RefObject<HTMLCanvasElement | null>;
  anonymous: boolean;
  /** The open round, or null when none is open. */
  item: RoundItem | null;
  onClose: () => void;
};

export function CardScanSheet({ ctl, canvas, anonymous, item, onClose }: Props) {
  const { t } = useTranslation();
  const [, setTick] = useState(0);

  useEffect(() => {
    const el = canvas.current;
    const off = ctl.subscribe(() => setTick((n) => n + 1));
    // the card rows first: no card joins before they are read (a no-op once they are)
    void ctl.readRows();
    return () => {
      off();
      // the photo and a decode under way go; the queued answers do not
      ctl.detach();
      // the drawn photo is the last copy this page holds: empty it
      if (el) {
        el.width = 0;
        el.height = 0;
      }
    };
  }, [ctl, canvas]);

  const gv: GatewayView = ctl.view();
  const phase: ScanPhase = ctl.phase;
  const r = reportOf(ctl, gv, item);
  const rows: RowsState = ctl.rows;

  return (
    <Sheet title={t("cards.scan.title")} lead={t("cards.scan.lead")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <canvas
          ref={canvas}
          width={0}
          height={0}
          role="img"
          aria-label={t("cards.scan.photoLabel")}
          className={phase === "shown" ? "h-auto w-full rounded-[var(--radius-card)]" : "hidden"}
        />
        <CardScanReport
          anonymous={anonymous}
          keys={r.keys}
          phase={phase}
          blocked={r.blocked}
          stopped={gv.stopped}
          notice={ctl.notice}
          summary={r.summary}
          previous={r.previous}
          photo={phase === "shown" ? ctl.photo : null}
          rows={rows}
          byNumber={r.byNumber}
          onRetry={() => void ctl.readRows()}
        />
        <div className="flex flex-col gap-2">
          <Button
            block
            disabled={rows !== "ready" || !!r.blocked || !!gv.stopped || !capabilities.cardCamera}
            loading={phase === "reading"}
            onClick={() => void ctl.take()}
          >
            {phase === "idle" ? t("cards.scan.take") : t("cards.scan.another")}
          </Button>
          <Button block variant="tertiary" onClick={onClose}>{t("dialog.close")}</Button>
        </div>
      </div>
    </Sheet>
  );
}

/** Decode a photo (`readPhotoWith`): upright or not, at most MAX_SIDE, in the worker, with the
 * full-size bitmap closed once a SHOWN_SIDE preview is drawn from it; `show` puts the preview
 * on the sheet's canvas with the marks. */
function readPhoto(blob: Blob, signal: AbortSignal, canvas: () => HTMLCanvasElement | null, anonymous: boolean): Promise<ReadPhoto> {
  return readPhotoWith<ImageBitmap, HTMLCanvasElement>(blob, signal, {
    bitmap: (b) => bitmapOf(b),
    pixels: pixelsOf,
    preview: (bitmap) => {
      const size = fitWithin(bitmap.width, bitmap.height, SHOWN_SIDE);
      const el = document.createElement("canvas");
      el.width = size.width;
      el.height = size.height;
      el.getContext("2d")?.drawImage(bitmap, 0, 0, size.width, size.height);
      return el;
    },
    closeBitmap: (bitmap) => bitmap.close(),
    decode: (pixels, s) => decodeOffThread(pixels, s),
    show: (preview, decodedWidth, scan) => drawMarks(canvas(), preview, decodedWidth, scan, anonymous),
    dropPreview: (preview) => {
      preview.width = 0;
      preview.height = 0;
    },
  });
}

/** The colours of the marks, from the design tokens. */
export type MarkColours = { ink: string; halo: string; text: string };

/** What `paintMarks` draws with: the part of a 2D context it uses. */
export type MarkPen = Pick<
  CanvasRenderingContext2D,
  "save" | "restore" | "setLineDash" | "beginPath" | "moveTo" | "lineTo" | "closePath" | "stroke" | "fillRect" | "fillText" | "measureText"
> & { lineWidth: number; strokeStyle: unknown; fillStyle: unknown; font: string; textAlign: string; textBaseline: string };

/** Draw the marks of `marksFor` at scale `k` (the shown width over the decoded width): an
 * outline per card seen in the accent with a halo, and in a named session the card's number in a
 * box. Colours come from the tokens only. */
export function paintMarks(pen: MarkPen, scan: CardScan, anonymous: boolean, k: number, width: number, colours: MarkColours) {
  const line = Math.max(2, width / 320);
  const marks = marksFor(scan, anonymous);
  for (const { corners, dashed } of marks.outlines) {
    pen.save();
    pen.setLineDash(dashed ? [line * 3, line * 2] : []);
    pen.lineWidth = line * 2.5;
    pen.strokeStyle = colours.halo;
    pen.beginPath();
    corners.forEach((p, i) => (i ? pen.lineTo(p.x * k, p.y * k) : pen.moveTo(p.x * k, p.y * k)));
    pen.closePath();
    pen.stroke();
    pen.lineWidth = line;
    pen.strokeStyle = colours.ink;
    pen.stroke();
    pen.restore();
  }
  for (const m of marks.labels) {
    // the card's number only, at its centre, in a box that reads on any photo
    const h = Math.max(12, Math.round((m.side * k) / 4));
    pen.font = `600 ${h}px sans-serif`;
    const w = pen.measureText(m.text).width;
    pen.fillStyle = colours.halo;
    pen.fillRect(m.x * k - w / 2 - 4, m.y * k - h / 2 - 3, w + 8, h + 6);
    pen.fillStyle = colours.text;
    pen.textAlign = "center";
    pen.textBaseline = "middle";
    pen.fillText(m.text, m.x * k, m.y * k);
  }
}

/** Put the preview on the sheet's canvas and draw the marks over it. `decodedWidth` is the width
 * the decoder read at, which its corners are in. */
function drawMarks(el: HTMLCanvasElement | null, preview: HTMLCanvasElement, decodedWidth: number, scan: CardScan, anonymous: boolean) {
  if (!el) return;
  el.width = preview.width;
  el.height = preview.height;
  const ctx = el.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(preview, 0, 0);
  const style = getComputedStyle(el);
  const token = (name: string) => style.getPropertyValue(name).trim();
  paintMarks(ctx, scan, anonymous, preview.width / decodedWidth, preview.width, { ink: token("--accent"), halo: token("--surface"), text: token("--ink") });
}
